import { ControlValueAccessor, FormControl, Validators } from '@angular/forms';

// Shared CVA base for the form-* components. The parent's FormControl is bound straight to the
// inner Material control, so the CVA hooks are mostly no-ops; this class owns error display.
export class BaseControlValueAccessor<T> implements ControlValueAccessor {
  public disabled = false;
  public value!: T;
  public control!: FormControl;

  protected getLabel(): string {
    return '';
  }

  public onChange(_newVal: T) {}
  public onTouched(_value?: unknown) {}

  public writeValue(obj: T): void {
    this.value = obj;
  }

  public registerOnChange(fn: (value: T) => void): void {
    this.onChange = fn;
  }

  public registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  public setDisabledState?(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  get isRequired(): boolean {
    return this.control?.hasValidator?.(Validators.required) ?? false;
  }

  get showError(): boolean {
    if (!this.control) return false;
    return (
      this.control.invalid &&
      (this.control.dirty || this.control.touched) &&
      this.errorKeys.length > 0
    );
  }

  get errorKeys(): string[] {
    if (!this.control?.errors) return [];
    return Object.keys(this.control.errors);
  }

  getErrorMessage(errorKey: string): string {
    const label = this.getLabel();
    switch (errorKey) {
      case 'required':
        return label ? `${label} is required` : 'Required';
      case 'email':
        return 'Must be a valid email';
      case 'message':
        return this.control?.getError('message') || 'Invalid';
      case 'minlength': {
        const minLen = this.control?.getError('minlength')?.requiredLength || '';
        return `Minimum ${minLen} characters required`;
      }
      case 'maxlength': {
        const maxLen = this.control?.getError('maxlength')?.requiredLength || '';
        return label
          ? `${label} cannot exceed ${maxLen} characters`
          : `Cannot exceed ${maxLen} characters`;
      }
      case 'min':
        return 'Value is below minimum';
      case 'max':
        return 'Value exceeds maximum';
      case 'pattern':
        return 'Invalid format';
      case 'equalTo':
        return 'Values do not match';
      default: {
        const errorValue = this.control?.getError(errorKey);
        return typeof errorValue === 'string' ? errorValue : 'Invalid';
      }
    }
  }
}
